<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { ref, computed, watch, getCurrentInstance } from 'vue'
import _ from 'lodash';
import { useNotification } from "@kyvg/vue3-notification";
import moment from 'moment-timezone';
import MediaflowService from '@/services/MediaflowService';
import IconBase from '@/components/icon/IconBase.vue'
import AddPlayoutErrorAlert from '@/components/TVPlayout/AddPlayoutErrorAlert.vue'
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import FormUpload from '../Atoms/FormUpload.vue'
import AlertModal from "@/components/modals/AlertModal.vue";
import Alert from '@/components/Atoms/Alert.vue';
import Sidebar from '../modals/Sidebar'
import FormSwitch from '@/components/Atoms/FormSwitch.vue';

const store = useStore()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const sidebar = ref()
const error = ref(null)

const props = defineProps({
  mediaflowId: {
    type: String,
    required: true
  },
  flowId: {
    type: String,
    required: true
  },
  media: {
    type: Object,
    required: true
  }, 
  streamAlive: {
    type: Boolean,
    default: false
  }
})

const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const updateMediaValidation = computed(() => {
  if (mediaData.value.name.length === 0) {
    return true
  }

  if (selectedMediaType.value.value === 'live' || selectedMediaType.value.value === 'vod') {
    return !selectedStream.value
  }

  if (selectedMediaType.value.value === 'image') {
    return !embedWatermarkTempImageId.value || !embedWatermarkTempUrl.value
  }

  if (selectedMediaType.value.value === 'url') {
    return !sourceUrl.value
  }

  return false
})
const imageUrl = computed(() => {
  if (embedWatermarkTemp.value) {
    return embedWatermarkTemp.value
  }

  return null
})

// const liveStream = computed(() => {
// 	if (!props.media.stream) return {};
//   return allInOneStreams.value.find(stream => stream._id === props.media.stream);
// })

const mediaData = ref({
  name: '',
  type: '',
})

const updateProcessing = ref(false)
const streamSourceDropdownVisible = ref(false)
const streamSourceDropdownRef = ref(null)

const selectedStream = ref(null)
const selectedMediaType = ref({
  label: 'Livestream',
  value: 'live'
})

const mediaTypes = [
  { label: 'Livestream', value: 'live' },
  { label: 'VOD', value: 'vod' },
  { label: 'Image', value: 'image' },
  { label: 'URL', value: 'url' },
]

const maxPosterUploadMB = ref(2)
const embedWatermarkTemp = ref(null)
const embedWatermarkTempImageId = ref(null)
const embedWatermarkTempUrl = ref(null)

const features = ref({
  embedWatermark: {
    error: false,
    enabled: false,
    value: null,
    valueType: 'string'
  }
})

const featureProcessing = ref({
  embedWatermark: false,
})

const liveRestartPlayback = ref(false)
const loopVOD = ref(false)
const restartPlaybackVOD = ref(false)
const showEmptyOnEndVOD = ref(false)

const sourceUrl = ref(null)

const emit = defineEmits(['media-updated'])

const onEmbedPosterPreview = (file, name) => {
  const selector = '#embed-watermark-input';
  const imageInput = document.querySelector(selector);
  const imageFile = imageInput.files[0];

  if (!imageFile) return;

  if (imageFile.type.indexOf('image/') === -1) {
    showModal('embed-upload-format');
    imageInput.value = null;
    return;
  }

  let maxSize = 2
  maxPosterUploadMB.value = maxSize;

  maxSize *= 1024 * 1024;

  if (imageFile.size > maxSize) {
    showModal('embed-image-size-exceed-media-edit');
    imageInput.value = null;
    return;
  }

  const validImageTypes = ['png', 'jpeg', 'gif', 'avif']
  const uploadedImageFileType = imageFile.type.replace('image/', '');
  if (
    validImageTypes &&
    validImageTypes.indexOf(uploadedImageFileType) === -1
  ) {
    showModal('embed-image-mime-type-media-edit');
    imageInput.value = null;
    return;
  }

  imageReader(imageFile, base64 => {
    embedWatermarkTemp.value = base64;
  })
}

const saveEmbedPoster = async (type) => {
  const featureProp = 'embedWatermark'
  features.value[featureProp].error = false;
  const selector = '#embed-watermark-input'
  const imageInput = document.querySelector(selector);

  const imageFile = imageInput.files[0];
  if (!imageInput.value || !imageFile) {
    features.value[featureProp].error = 'please pick an image file';
    return;
  }

  featureProcessing.value[featureProp] = true;
  const fdata = new FormData();
  fdata.append('file', imageFile);
  const res = await MediaflowService.uploadFlowImage(
    props.mediaflowId,
    props.flowId,
    fdata
  );
  if (res.success) {
    embedWatermarkTempImageId.value = res.imageId
    embedWatermarkTempUrl.value = res.imageUrl

    notify({
      group: 'success',
      text: `image uploaded successfully.`
    });
    imageInput.value = ''
  } else {
    features.value[featureProp].error = 'Unable to upload the image. Please try again later.';
  }
  featureProcessing.value[featureProp] = false;
}

const cancelUpload = async (type) => {
  const selector = '#embed-watermark-input'
  const previewFlag = embedWatermarkTemp.value 
  if (previewFlag) {
    embedWatermarkTemp.value = null;
    document.querySelector(selector).value = null;
  }
  removeImage(true);
}

const removeImage = async (toast = false) => {
  if (!embedWatermarkTempImageId.value) {
    return
  }

  try {
    await MediaflowService.deleteFlowImage(
      props.mediaflowId,
      props.flowId,
      embedWatermarkTempImageId.value
    )
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null

    if (toast) {
      notify({ group: 'success', text: 'Image media removed' })
    }
    const settingsProp = 'embedWatermark'
    features.value[settingsProp].value = null;
  } catch (e) {
    console.log(e);
  }
}

function imageReader(file, cb) {
  if (!file) {
    return;
  }
  var reader = new FileReader();
  reader.onload = function () {
    if (cb) {
      cb(reader.result);
    }
  };
  reader.readAsDataURL(file);
}

const closeModal = (() => {
  sidebar.value.closeSidebar()
})

const toggleSidebar = () => {
  sidebar.value.toggleSidebar()
  if (props.media) {
    onSidebarOpened()
  }
}

const onReset = (() => {
  mediaData.value = {
    name: '',
    type: '',
  }
  error.value = null
  selectedStream.value = null

  streamSourceDropdownRef.value?.closeDropdown()
  streamSourceDropdownVisible.value = false
})

const initializeMediaData = () => {
  console.log('initializeMediaData', props.media)
  if (props.media) {
    selectedMediaType.value = mediaTypes.find(item => item.value === props.media.type);

    mediaData.value = {
      name: props.media.name || '',
      type: props.media.type || '',
    }

    if (props.media.type === 'live') {
      selectedStream.value = allInOneStreams.value.find(stream => stream._id === props.media.stream);
      
      liveRestartPlayback.value = props.media.liveRestartPlayback ?? false
    }

    if (props.media.type === 'vod') {
      selectedStream.value = { name: props.media?.fileName ?? props.media.file, source: 'dynamic', bytes: 1, type: 'vod' } // source & bytes for icon

      loopVOD.value = props.media.loop ?? false
      restartPlaybackVOD.value = props.media.restartPlayback ?? false
      showEmptyOnEndVOD.value = props.media.showEmptyOnEnd ?? false
    }

    if (props.media.type === 'image') {
      embedWatermarkTempImageId.value = props.media.imageId
      embedWatermarkTempUrl.value = props.media.imageUrl
      embedWatermarkTemp.value = props.media.imageUrl
    }

    if (props.media.type === 'url') {
      sourceUrl.value = props.media.sourceUrl
    }
  }
}

const updateMedia = async () => {
  updateProcessing.value = true
  try {
    const updateData = {
      name: mediaData.value.name,
      type: selectedMediaType.value.value,
    }

    if (updateData.type === 'live') {
      updateData.stream = selectedStream.value._id
      updateData.liveRestartPlayback = liveRestartPlayback.value
    }

    if (updateData.type === 'vod') {
      // vod not changed
      if (selectedStream.value.source === 'dynamic') {
        console.log('vod not changed')
        selectedStream.value = props.media
      }

      updateData.stream = selectedStream.value.file?._id
      updateData.key = selectedStream.value.folder?.key
      updateData.file = selectedStream.value.file?.id
      updateData.fileName = selectedStream.value.file?.fileName
      updateData.embedUrl = selectedStream.value.file?.embedUrl

      updateData.loop = loopVOD.value
      updateData.restartPlayback = restartPlaybackVOD.value
      updateData.showEmptyOnEnd = showEmptyOnEndVOD.value
    }

    if (updateData.type === 'image') {
      updateData.imageId = embedWatermarkTempImageId.value
      updateData.imageUrl = embedWatermarkTempUrl.value
    }

    if (updateData.type === 'url') {
      updateData.sourceUrl = sourceUrl.value
    }

    const updatedMedias = await MediaflowService.updateFlowMedia(
      props.mediaflowId,
      props.flowId,
      props.media.id,
      updateData
    );

    updateProcessing.value = false

    if (Array.isArray(updatedMedias) && updatedMedias.length > 0) {
      const media = updatedMedias.find(item => item.id === props.media.id);
      emit('media-updated', media)
      console.log('updatedMedia', media)
    }

    notify({ group: 'success', text: 'Media Updated Successfully' })
    closeModal()
  } catch (err) {
    updateProcessing.value = false
    console.log(err);
    error.value = err.message
  }
}

const onSourceDropdownShow = () => {
  streamSourceDropdownVisible.value = true
}

const onSourceDropdownHide = () => {
  streamSourceDropdownVisible.value = false
}

const onSourceStreamSelected = async (stream) => {
  selectedStream.value = stream
}

const onSidebarClosed = () => {
  onReset()
}

const onSidebarOpened = () => {
  initializeMediaData()
}

const onMediaTypecSelect = (typ) => {
  selectedMediaType.value = typ
  selectedStream.value = null
}

watch(() => props.media, () => {
  if (props.media) {
    initializeMediaData()
  }
}, { immediate: true })

defineExpose({ toggleSidebar })

</script>

<template>
  <Sidebar ref="sidebar" :stage="1" @closed="onSidebarClosed" width="md:w-[420px]">
    <template v-slot:stage-one>
      <div class="px-4 h-full overflow-y-auto">
        <h3 class="text-lg font-medium mb-2 mt-3">Edit Media</h3>
        <p class="text-dark-7 mb-4 text-sm">Update media settings</p>
        <AddPlayoutErrorAlert :error="error" v-if="error" />
          <div class="mb-3">
            <label class="text-dark-9 text-tiny">
              Name
            </label>
            <FormInput :disabled="streamAlive" id="mediaName" v-model="mediaData.name" placeholder="Media name" />
          </div>
          
          <div class="px-3 mb-3">
            <label class="text-dark-9 text-tiny">Type</label>
            <drop-down-menu type="flat" size="md" class="w-full" customClasses="flex items-center justify-between font-normal" listClasses="dropdown-menu_md w-full" 
              :disabled="streamAlive"
            >
<template #toggle-button>
                <span>
                  <span class="type-name">{{ selectedMediaType.label }}</span>
                </span>
              </template>

<template #menu-items>
<drop-down-menu-item 
                v-for="option in mediaTypes" :key="option.value"  
                @click="onMediaTypecSelect(option)">
                <span class="ml-2">{{ option.label }}</span>
              </drop-down-menu-item>
</template>
</drop-down-menu>
          </div>

          <div v-if="selectedMediaType.value === 'live'" class="px-3 mb-3">
            <label class="text-dark-9 text-tiny">Livestream</label>
            <stream-source-dropdown 
              ref="streamSourceDropdownRef"
              @on-show="onSourceDropdownShow" 
              @on-hide="onSourceDropdownHide"
              @on-source-selected="onSourceStreamSelected" 
              :dropRight="false" 
              :onlyLiveSelector="true" 
              :dropdownDisabled="false"
              source="multiview"
              :refLink="0"
              :selectedStream="selectedStream"
						  />
          </div>

          <div v-if="selectedMediaType.value === 'live'" class="px-3 mb-3">
            <FormSwitch size="md" label="Restart playback once scene becomes active" labelPosition="right" v-model="liveRestartPlayback" />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
            <label class="text-dark-9 text-tiny">Video from VODs</label>
            <stream-source-dropdown 
              ref="streamSourceDropdownRef"
              @on-show="onSourceDropdownShow" 
              @on-hide="onSourceDropdownHide"
              @on-source-selected="onSourceStreamSelected" 
              :dropRight="false" 
              source="mediaflow"
              :showPlaylist="false"
              :hideLive="true"
              :refLink="0"
              :selectedStream="selectedStream"
						  />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
            <FormSwitch size="md" label="Loop" labelPosition="right" v-model="loopVOD" />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
            <FormSwitch size="md" label="Restart playback once scene becomes active" labelPosition="right" v-model="restartPlaybackVOD" />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
            <FormSwitch size="md" label="Show nothing when playback ends" labelPosition="right" v-model="showEmptyOnEndVOD" />
          </div>

          <div v-if="selectedMediaType.value === 'image'" class="px-3 mb-3">
            <div class="basis-full md:basis-auto">
              <label class="text-dark-9 text-tiny">Upload Image</label>
            </div>
            <div class="flex justify-between items-center gap-x-2">
              <div class="flex">
                <img v-if="imageUrl" width="100px" :src="imageUrl" class="w-full size-full md:w-[100px]"  />
                <p v-else class="text-tiny text-surface-6">No file chosen</p>
              </div>

              <div class="flex">
                <FormUpload :class="{hidden: embedWatermarkTemp }" name="watermark" id="embed-watermark-input" placeholder="No file chosen" type="secondary"
                  :label="embedWatermarkTemp ? 'Change' : 'Select image'" :on-file-change="onEmbedPosterPreview" />
                <FormButton v-if="embedWatermarkTemp"  :class="{hidden: embedWatermarkTempImageId}" :disabled="featureProcessing.embedWatermark"
                  @click="saveEmbedPoster('watermark')">
                  {{ featureProcessing.embedWatermark ? 'Saving' : 'Save' }}
                </FormButton>
                <FormButton v-if="embedWatermarkTemp || features.embedWatermark.value"
                  class="ml-2"
                  :disabled="featureProcessing.embedWatermark" type="secondary" @click="cancelUpload('watermark')">
                  <span v-if="embedWatermarkTemp">Cancel</span>
                  <span v-else>Remove</span>
                </FormButton>
              </div>
            </div>
            <Alert v-show="features.embedWatermark.error" class="alert alert-danger">
              {{ features.embedWatermark.error }}
            </Alert>
          </div>

          <div v-if="selectedMediaType.value === 'url'" class="px-3 mb-3">
            <label class="text-dark-9 text-tiny">URL</label>
            <FormInput v-model="sourceUrl" placeholder="https://.." />
          </div>
          
          <div>
            <div class="text-center">
              <FormButton 
                @click="updateMedia" 
                :disabled="updateMediaValidation"
                type="main" 
                size="md"
                class="w-full mt-2 justify-center"
              >
                 {{ updateProcessing ? 'Saving..' : 'Update' }}
              </FormButton>
              <FormButton 
                type="secondary" 
                size="md" 
                class="w-full mb-3 mt-2 justify-center"
                @click="closeModal"
              >
                Cancel
              </FormButton>
            </div>
          </div>
      </div>
    </template>
  </Sidebar>

  <alert-modal :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'"
    modal-id="embed-image-size-exceed-media-edit" />
  <alert-modal modal-id="embed-image-mime-type-media-edit" message="Only PNG/JPG/GIF/AVIF image files are allowed" />
</template>